
const libPictView = require('pict-view');

const html = String.raw;

const EXPLORER_HASH = 'SamplePlanExplorer';
const TREE_MOUNT = '#SamplePlan-Tree';
const DETAIL_MOUNT = '#SamplePlan-Detail';

const fEsc = (pValue) => String(pValue == null ? '' : pValue).replace(/[&<>]/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[pChar]));
const fMeterHTML = (pMeters) => (pMeters || []).map((pMeter) => (pMeter.Kind === 'bar')
	? `<span class="pdex-meter pdex-meter-${pMeter.Tone}" title="${fEsc(pMeter.Title)}"><span class="pdex-meter-track"><span class="pdex-meter-fill" style="width: ${pMeter.Percent}%;"></span></span><span class="pdex-meter-text">${fEsc(pMeter.Label)}</span></span>`
	: `<span class="pdex-pill pdex-pill-${pMeter.Tone}" title="${fEsc(pMeter.Title)}">${fEsc(pMeter.Text)}</span>`).join(' ');
const fField = (pLabel, pValue) => ((pValue == null || pValue === '') ? '' : `<div class="sp-d-field"><div class="sp-d-flabel">${fEsc(pLabel)}</div><div class="sp-d-fval">${fEsc(pValue)}</div></div>`);
// A full-width read-only field for the long-form text columns (descriptions, instructions, remarks).
const fFieldWide = (pLabel, pValue) => ((pValue == null || pValue === '') ? '' : `<div class="sp-d-field sp-d-field-wide"><div class="sp-d-flabel">${fEsc(pLabel)}</div><div class="sp-d-fval sp-d-fval-long">${fEsc(pValue)}</div></div>`);

// Attribute-safe escaping (fEsc only covers text nodes — attribute values also need quotes escaped).
const fAttr = (pValue) => String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));

// --- Edit-mode input helpers (parallel vocabulary to fField). Every control writes into the view's edit buffer via
//     _Pict.views.SamplePlan.onEditField(field, value); text/number inputs use oninput so live counts stay current. ---
const fInput = (pLabel, pField, pValue, pType, pStep) =>
	`<div class="sp-d-editrow"><label class="sp-d-flabel">${fEsc(pLabel)}</label>`
	+ `<input class="sp-d-input" type="${pType || 'text'}"${(pType === 'number') ? ` step="${fAttr(pStep || 'any')}"` : ''} value="${fAttr(pValue)}" `
	+ `oninput="_Pict.views.SamplePlan.onEditField('${pField}', this.value)" /></div>`;

const fTextarea = (pLabel, pField, pValue) =>
	`<div class="sp-d-editrow sp-d-field-wide"><label class="sp-d-flabel">${fEsc(pLabel)}</label>`
	+ `<textarea class="sp-d-textarea" rows="2" oninput="_Pict.views.SamplePlan.onEditField('${pField}', this.value)">${fEsc(pValue)}</textarea></div>`;

const fSelect = (pLabel, pField, pValue, pOptions) =>
{
	const tmpOptions = (pOptions || []).map((pOption) =>
	{
		const tmpValue = (pOption && (typeof pOption === 'object')) ? pOption.value : pOption;
		const tmpText = (pOption && (typeof pOption === 'object')) ? pOption.label : pOption;
		return `<option value="${fAttr(tmpValue)}"${(String(tmpValue) === String(pValue)) ? ' selected' : ''}>${fEsc(tmpText)}</option>`;
	}).join('');
	return `<div class="sp-d-editrow"><label class="sp-d-flabel">${fEsc(pLabel)}</label>`
		+ `<select class="sp-d-select" onchange="_Pict.views.SamplePlan.onEditField('${pField}', this.value)">${tmpOptions}</select></div>`;
};

// A native combobox (free text + a suggestion list) — the faithful equivalent of the legacy filterable comboboxes.
const fCombo = (pLabel, pField, pValue, pOptions, pListID) =>
{
	const tmpOptions = (pOptions || []).map((pOption) => `<option value="${fAttr(pOption)}"></option>`).join('');
	return `<div class="sp-d-editrow"><label class="sp-d-flabel">${fEsc(pLabel)}</label>`
		+ `<input class="sp-d-input" list="${pListID}" value="${fAttr(pValue)}" oninput="_Pict.views.SamplePlan.onEditField('${pField}', this.value)" />`
		+ `<datalist id="${pListID}">${tmpOptions}</datalist></div>`;
};

/**
 * Host page for the Sample Plan editor. Renders the chrome (project header + Docs toggle + a master/detail
 * split) into the shared application container; the SamplePlan provider supplies the in-memory ExplorerConfig
 * (pict-dataexplorer renders the tree into the left pane) and the selected-record detail data. Like the Data
 * Explorer host, the explorer is created in onAfterRender (the mount div + post-login record cards must exist
 * first); createExplorer is idempotent so re-renders just rebuild from the current config.
 */
class SamplePlan extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		// Detail panel view/edit state. Keyed off the current selection so moving to another row resets to view mode.
		this._detailKey = null;
		this._detailMode = 'view';
		this._editBuffer = null;
		this._editError = '';
		this._editSaving = false;
		this._detailFetchFailed = false;
		// Front-door project list state.
		this._projectFilterTimer = null;
		this._projectLoadToken = 0;
		// Add-entry / add-material state.
		this._explorer = null;
		this._addMode = null;      // null | 'entry' | 'material'
		this._addBuffer = null;    // the new-entry draft (add-entry mode)
		this._addTarget = null;    // the Material (add-entry) or LineItem (add-material) being added to
		this._addError = '';
		this._addSaving = false;
		this._materialFilterTimer = null;
		this._materialLoadToken = 0;
		this._addEntryToken = 0;
		// When embedded in a host (the ProjectWorkspace Materials ▸ Sample Plan tab) this holds that host selector,
		// and every render — including provider-triggered repaints — targets it instead of the app container.
		this._embedDestination = null;
	}

	get provider() { return this.pict.providers['SamplePlan']; }

	/** Render into the embed host when embedded (so provider `_render()` repaints land in the Materials tab, not the
	 *  app container). An explicit destination argument always wins. */
	render(pRenderableHash, pRenderDestinationAddress, pTemplateRecordAddress, pRootRenderable)
	{
		const tmpDestination = (typeof pRenderDestinationAddress === 'string') ? pRenderDestinationAddress : (this._embedDestination || undefined);
		return super.render(pRenderableHash, tmpDestination, pTemplateRecordAddress, pRootRenderable);
	}

	/** Embed this plan into a host element for a fixed project (skips the front-door picker). ProjectWorkspace's
	 *  Materials ▸ Sample Plan tab calls this after each paint; it only reloads when the loaded project differs. */
	openInto(pIDProject, pHostSelector)
	{
		this._embedDestination = pHostSelector;
		const tmpState = (this.pict.AppData && this.pict.AppData.SamplePlan) || {};
		if (this.provider && (!tmpState.HasModel || (Number(this.provider._idProject) !== Number(pIDProject))))
		{
			this.provider.loadAsync(pIDProject);   // provider._render → this.render → the host
		}
		else { this.render(); }   // already loaded for this project — just (re)paint into the host
	}

	/** Leave embedded mode — the standalone #/SamplePlan screen renders to the app container again. */
	exitEmbed() { this._embedDestination = null; }

	onBeforeRender(pRenderable)
	{
		const tmpState = (this.pict.AppData && this.pict.AppData.SamplePlan) || {};
		// Mutually-exclusive body modes, driven by the single-element-array trick.
		tmpState.LoadingSlot = tmpState.Loading ? [ tmpState ] : [];
		tmpState.ErrorSlot = (!tmpState.Loading && tmpState.Message) ? [ tmpState ] : [];
		tmpState.PickerSlot = (!tmpState.Loading && !tmpState.Message && !tmpState.HasModel) ? [ tmpState ] : [];
		tmpState.PlanSlot = (!tmpState.Loading && tmpState.HasModel) ? [ tmpState ] : [];
		tmpState.TitleText = (tmpState.HasModel && tmpState.ProjectName) ? `Sample Plan — ${tmpState.ProjectName}` : 'Sample Plan';
		tmpState.SubText = tmpState.HasModel
			? this._planSubText(tmpState)
			: 'Plan a project’s material sampling and testing across its line items.';
		tmpState.EmbedClass = this._embedDestination ? 'sp-embedded' : '';
		this.pict.AppData.SamplePlan = tmpState;
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable)
	{
		const tmpProvider = this.provider;
		const tmpExplorerProvider = this.pict.providers['Pict-DataExplorer'];
		const tmpConfig = tmpProvider ? tmpProvider.getExplorerConfig() : null;
		if (tmpConfig && tmpExplorerProvider)
		{
			const tmpModel = tmpProvider.model;
			// No configured sample plan (no pay-item sections and no line items) → show an empty state instead of the two
			// always-present "By Pay-Item Section" / "By Line Item" folders expanding to nothing.
			if (tmpModel && !(tmpModel.sections || []).length && !(tmpModel.lineItems || []).length)
			{
				this.pict.ContentAssignment.assignContent(TREE_MOUNT,
					'<div class="sp-d-empty"><i class="fas fa-flask"></i> No sample plan configured for this project yet.'
					+ ' Add materials and sampling rules to its line items to build one.</div>');
				this._explorer = null;
				this.renderDetail();
				this.pict.CSSMap.injectCSS();
				return super.onAfterRender(pRenderable);
			}
			const tmpExplorer = tmpExplorerProvider.createExplorer(EXPLORER_HASH, Object.assign({ DestinationAddress: TREE_MOUNT }, tmpConfig));
			// The tiers are synthesized in memory (custom Resolvers), so there is no Meadow entity schema to
			// discover: the default schema source GETs `/1.0/<Entity>/Schema` — a 404 for the synthetic 'Rule'
			// tier ("Endpoint does not exist!", and so no column chooser at all there) and, for the real
			// entities, offers columns the in-memory records don't carry (they render blank when checked — the
			// "only Description and Units show up" symptom). Point the "Columns" chooser at each tier's real
			// record fields instead; no server round-trip, and every offered column exists on the record.
			tmpExplorer.SchemaSource = (pEntity, fCallback) => fCallback(null, tmpProvider.chooserColumnsForEntity(pEntity));
			// The whole plan is held in memory and material/section/rule nodes are keyed synthetically (e.g. a
			// material's Key is "IDLineItem:IDMaterial"), so the explorer's default ⓘ card — which re-fetches the
			// record from the server by the node id — both 404s and is unnecessary. Render the preview card straight
			// from the node's own in-memory record instead (Rich:false skips the fetch; the default card still
			// renders its dynamic body from whatever record it is handed).
			tmpExplorer.openCardForNode = function (pKey, pAnchorElement)
			{
				const tmpNode = this._node(pKey);
				const tmpManager = this.pict.providers.RecordSetCardManager;
				if (!tmpNode || !tmpManager || (typeof tmpManager.openCard !== 'function')) { return; }
				tmpManager.openCard(tmpNode.Entity, tmpNode.Record, pAnchorElement, { Rich: false, Audit: false, EntityConfig: tmpNode.EntityConfig });
			};
			tmpExplorer.renderExplorer();
			this._explorer = tmpExplorer;   // retained so add-entry / add-material can refresh a single subtree in place
			this.renderDetail();
		}
		else
		{
			// No plan loaded → the front-door project list is showing; populate it (with any typed filter preserved).
			this._initProjectPicker();
		}
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable);
	}

	// --- Front-door: a filterable list of projects (replaces the raw "type an IDProject" box) -----------

	/** Populate the project list mount, if present, seeding from whatever is currently in the filter box. */
	_initProjectPicker()
	{
		const tmpMount = this.pict.ContentAssignment.getElement('#SamplePlan-ProjectList');
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		const tmpInput = document.getElementById('SamplePlan-ProjectFilter');
		this._loadProjects(tmpInput ? tmpInput.value : '');
	}

	/** Debounced filter handler — server-searches projects by name and re-renders just the list (input keeps focus). */
	onProjectFilter(pValue)
	{
		if (this._projectFilterTimer) { clearTimeout(this._projectFilterTimer); }
		this._projectFilterTimer = setTimeout(() => { this._loadProjects(pValue); }, 200);
	}

	/** Fetch the (filtered) project list and render it; a token guards against out-of-order responses. */
	_loadProjects(pTerm)
	{
		const tmpProvider = this.provider;
		if (!tmpProvider) { return; }
		const tmpToken = (this._projectLoadToken = (this._projectLoadToken || 0) + 1);
		tmpProvider.searchProjectsAsync(pTerm).then((pProjects) =>
		{
			if (tmpToken === this._projectLoadToken) { this._renderProjectList(pProjects, pTerm); }
		}).catch(() =>
		{
			if (tmpToken === this._projectLoadToken) { this.pict.ContentAssignment.assignContent('#SamplePlan-ProjectList', '<div class="sp-proj-note sp-proj-err">Could not load projects.</div>'); }
		});
	}

	/** Render the project rows into the list mount. */
	_renderProjectList(pProjects, pTerm)
	{
		const tmpMount = this.pict.ContentAssignment.getElement('#SamplePlan-ProjectList');
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		const tmpList = Array.isArray(pProjects) ? pProjects : [];
		if (!tmpList.length)
		{
			const tmpTerm = String(pTerm == null ? '' : pTerm).trim();
			this.pict.ContentAssignment.assignContent('#SamplePlan-ProjectList', `<div class="sp-proj-note">${tmpTerm ? `No projects match “${fEsc(tmpTerm)}”.` : 'No projects available.'}</div>`);
			return;
		}
		const tmpHeader = '<div class="sp-proj-head"><span class="sp-proj-name">Project</span><span class="sp-proj-year">Spec Year</span><span class="sp-proj-go"></span></div>';
		const tmpRows = tmpList.map((pProject) =>
		{
			const tmpYear = pProject.SpecYear ? fEsc(pProject.SpecYear) : '—';
			return `<button type="button" class="sp-proj-row" onclick="_Pict.views.SamplePlan.openProject(${Number(pProject.IDProject)})">`
				+ `<span class="sp-proj-name">${fEsc(pProject.Name)}</span>`
				+ `<span class="sp-proj-year" title="Specification year (from the project’s contract)">${tmpYear}</span>`
				+ `<span class="sp-proj-go"><i class="fas fa-chevron-right"></i></span></button>`;
		}).join('');
		const tmpCapNote = (tmpList.length >= 100) ? '<div class="sp-proj-note sp-proj-cap">Showing the first 100 — type to narrow the list.</div>' : '';
		this.pict.ContentAssignment.assignContent('#SamplePlan-ProjectList', tmpHeader + tmpRows + tmpCapNote);
	}

	/** Open a project's sample plan. */
	openProject(pIDProject)
	{
		const tmpID = Number(pIDProject);
		if (Number.isFinite(tmpID) && (tmpID > 0)) { window.location.hash = `#/SamplePlan/${tmpID}`; }
	}

	/** The "Docs" button: pop the inline-documentation side panel open, showing the current selection's docs
	 *  (or the navigable spec index when nothing doc-relevant is selected). */
	openDocs()
	{
		// Suppress the app's route-aware help for this open — we drive the panel's content, not the app docs.
		const tmpApp = this.pict.PictApplication;
		if (tmpApp) { tmpApp._helpSuppressRouteDoc = true; }
		this._setHelpPanel(true);
		if (tmpApp) { tmpApp._helpSuppressRouteDoc = false; }
		this._renderCurrentDocs();
	}

	_panelOpen()
	{
		const tmpApp = this.pict.PictApplication;
		return !!(tmpApp && tmpApp._helpPanelOpen);
	}

	// --- Detail panel -------------------------------------------------------------------------------

	renderDetail()
	{
		const tmpElement = this.pict.ContentAssignment.getElement(DETAIL_MOUNT);
		if (!tmpElement || (tmpElement.length === 0)) { return; }
		this._syncSelectionState();
		this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, this._detailHTML());
		this._syncDocsPanel();
	}

	/**
	 * On a new selection: drop any in-progress edit (never carry an edit buffer between rows) and, for a rule, kick off
	 * the full-record fetch so VIEW can show every field. Browsing stays instant — the summary paints now and the panel
	 * re-paints when the full record arrives.
	 */
	_syncSelectionState()
	{
		const tmpProvider = this.provider;
		const tmpSelection = tmpProvider ? tmpProvider.selected : null;
		const tmpKey = tmpSelection ? tmpSelection.Key : null;
		if (tmpKey === this._detailKey) { return; }
		this._detailKey = tmpKey;
		this._detailMode = 'view';
		this._editBuffer = null;
		this._editError = '';
		this._editSaving = false;
		this._detailFetchFailed = false;
		// Moving to another node abandons any in-progress add (never carry a draft between rows).
		this._addMode = null;
		this._addBuffer = null;
		this._addTarget = null;
		this._addError = '';
		this._addSaving = false;
		this._addEntryToken = (this._addEntryToken || 0) + 1;   // invalidate any in-flight add-entry vocab load
		if (tmpSelection && (tmpSelection.Entity === 'Rule') && !tmpProvider.getRawRule((tmpSelection.Record || {}).IDRule))
		{
			tmpProvider.fetchRuleRecordAsync(tmpSelection).then((pRecord) =>
			{
				// Re-paint only if this is still the current selection and we haven't since entered edit mode.
				if (tmpProvider.selected && (tmpProvider.selected.Key === tmpKey) && (this._detailMode === 'view'))
				{
					if (!pRecord) { this._detailFetchFailed = true; }
					this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, this._detailHTML());
				}
			}).catch(() =>
			{
				// Full detail is best-effort, but a hard failure should read as an error, not a perpetual spinner.
				if (tmpProvider.selected && (tmpProvider.selected.Key === tmpKey) && (this._detailMode === 'view'))
				{
					this._detailFetchFailed = true;
					this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, this._detailHTML());
				}
			});
		}
	}

	/** Retry a failed full-detail fetch for the current rule selection. */
	retryDetailLoad()
	{
		const tmpProvider = this.provider;
		const tmpSelection = tmpProvider ? tmpProvider.selected : null;
		if (!tmpSelection || (tmpSelection.Entity !== 'Rule')) { return; }
		this._detailFetchFailed = false;
		this._editError = '';
		this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, this._detailHTML());
		tmpProvider.fetchRuleRecordAsync(tmpSelection).then((pRecord) =>
		{
			if (tmpProvider.selected && (tmpProvider.selected.Key === tmpSelection.Key) && (this._detailMode === 'view'))
			{
				if (!pRecord) { this._detailFetchFailed = true; }
				this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, this._detailHTML());
			}
		}).catch(() =>
		{
			if (tmpProvider.selected && (tmpProvider.selected.Key === tmpSelection.Key) && (this._detailMode === 'view'))
			{
				this._detailFetchFailed = true;
				this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, this._detailHTML());
			}
		});
	}

	/** The plan header subtitle: structural counts + the total sample-plan entries + (only when relevant) sample progress. */
	_planSubText(pState)
	{
		const tmpEntries = pState.RuleCount || 0;
		const tmpEntryLabel = (tmpEntries === 1) ? 'entry' : 'entries';
		const tmpSamples = pState.PlanText ? ` · ${pState.PlanText}` : '';
		return `${pState.SectionCount || 0} sections · ${pState.LineItemCount || 0} line items · ${tmpEntries} sample plan ${tmpEntryLabel}${tmpSamples}`;
	}

	/** Refresh the header subtitle in place (the plan total changes after a save; tree meters lag until reload). */
	_refreshHeader()
	{
		const tmpState = this.provider ? this.provider.workspaceState : null;
		if (!tmpState || !tmpState.HasModel) { return; }
		const tmpSubtitle = document.querySelector('.sampleplan-sub');
		if (tmpSubtitle) { tmpSubtitle.textContent = this._planSubText(tmpState); }
	}

	/**
	 * Keep the docs side panel in sync with the current selection — but only when the panel is already open
	 * (the Docs button opens it). Documentation renders in the inline-documentation panel, never in the record.
	 */
	_syncDocsPanel()
	{
		if (this._panelOpen()) { this._renderCurrentDocs(); }
	}

	/** Render the current selection's docs into the side panel — or the navigable spec index when nothing relevant is selected. */
	_renderCurrentDocs()
	{
		const tmpProvider = this.provider;
		const tmpSelection = tmpProvider ? tmpProvider.selected : null;
		const tmpEntries = tmpSelection ? tmpProvider.docHashesForSelection(tmpSelection) : [];
		if (!tmpSelection || !tmpEntries.length) { this._showSpecIndex(); return; }
		this._displayDocs(this._docNavHTML() + this._docsEditBarHTML(tmpSelection) + this._parseMd(this._docsMarkdownForSelection(tmpSelection)));
		tmpProvider.ensureSelectionDocs(tmpSelection).then((pChanged) =>
		{
			if (pChanged && tmpProvider.selected && (tmpProvider.selected.Key === tmpSelection.Key) && this._panelOpen())
			{
				this._displayDocs(this._docNavHTML() + this._docsEditBarHTML(tmpSelection) + this._parseMd(this._docsMarkdownForSelection(tmpSelection)));
			}
		});
	}

	/** The navigable root: every seeded sampling-manual doc (procedures + section schedules), grouped + linked. */
	_showSpecIndex()
	{
		const tmpProvider = this.provider;
		if (!tmpProvider) { return; }
		this._setHelpPanel(true);
		this._displayDocs('<div class="sp-docidx-loading">Loading sampling-manual index…</div>');
		tmpProvider.loadSpecIndex().then((pList) => { if (this._panelOpen()) { this._displayDocs(this._specIndexHTML(pList)); } });
	}

	/** A single doc-by-hash view, reached from the spec index. */
	openSpecDoc(pHash)
	{
		const tmpProvider = this.provider;
		if (!tmpProvider) { return; }
		this._setHelpPanel(true);
		this._displayDocs(this._docNavHTML() + '<div class="sp-docidx-loading">Loading…</div>');
		tmpProvider.ensureDoc(pHash).then(() =>
		{
			if (!this._panelOpen()) { return; }
			const tmpDoc = tmpProvider.getCachedDoc(pHash);
			this._displayDocs(this._docNavHTML() + this._parseMd((tmpDoc && tmpDoc.Content) ? tmpDoc.Content : '_Document not found._'));
		});
	}

	/** Assemble one markdown document from a selection's populated AcademyContent doc tiers. */
	_docsMarkdownForSelection(pSelection)
	{
		const tmpProvider = this.provider;
		const tmpRecord = pSelection.Record || {};
		const tmpEntries = tmpProvider.docHashesForSelection(pSelection);
		const tmpHeader = `# ${tmpRecord.Title || tmpRecord.Name || tmpRecord.TreeLabel || pSelection.Entity}\n`;
		if (tmpEntries.some((pEntry) => tmpProvider.getCachedDoc(pEntry.Hash) === undefined)) { return `${tmpHeader}\n_Loading documentation…_`; }
		const tmpParts = [];
		tmpEntries.forEach((pEntry) =>
		{
			const tmpDoc = tmpProvider.getCachedDoc(pEntry.Hash);
			if (tmpDoc && tmpDoc.Content) { tmpParts.push(`## ${pEntry.Tier}\n\n${tmpDoc.Content}`); }
		});
		return tmpParts.length ? `${tmpHeader}\n${tmpParts.join('\n\n---\n\n')}` : `${tmpHeader}\n_No documentation entries for this yet — browse the full manual below._`;
	}

	/** The grouped, linked root index (hand-built HTML so the links carry click handlers reliably). */
	_specIndexHTML(pList)
	{
		const fEscape = (pStr) => String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
		const fLink = (pDoc) => `<li><a href="#" onclick="_Pict.views.SamplePlan.openSpecDoc('${fEscape(pDoc.Hash)}'); return false;">${fEscape(pDoc.Title || pDoc.Hash)}</a></li>`;
		const tmpList = Array.isArray(pList) ? pList : [];
		const tmpProcedures = tmpList.filter((pDoc) => pDoc.Hash.indexOf('SamplePlanProcedure-') === 0).sort((pA, pB) => pA.Hash.localeCompare(pB.Hash));
		const tmpSections = tmpList.filter((pDoc) => pDoc.Hash.indexOf('SamplePlanSpec-') === 0).sort((pA, pB) => pA.Hash.localeCompare(pB.Hash, undefined, { numeric: true }));
		let tmpHTML = '<div class="sp-docidx"><h1>Materials Sampling Manual</h1>';
		if (tmpProcedures.length) { tmpHTML += `<h2>Sampling procedures</h2><ul>${tmpProcedures.map(fLink).join('')}</ul>`; }
		if (tmpSections.length) { tmpHTML += `<h2>Section schedules</h2><ul>${tmpSections.map(fLink).join('')}</ul>`; }
		if (!tmpProcedures.length && !tmpSections.length) { tmpHTML += '<p>No sampling-manual documents are seeded yet.</p>'; }
		return tmpHTML + '</div>';
	}

	_docNavHTML()
	{
		return '<div class="sp-docnav"><a href="#" onclick="_Pict.views.SamplePlan._showSpecIndex(); return false;">📖 All sampling docs</a></div>';
	}

	/**
	 * When the user has documentation-edit rights, offer a per-tier Edit control for the selection's docs — reusing the
	 * app's existing platform-doc editor (editPlatformHelpDoc). The project-specific entry (SamplePlanEntry-{IDRule}) is
	 * the one most worth editing; the canonical/override split + save are all handled by the shared machinery.
	 */
	_docsEditBarHTML(pSelection)
	{
		const tmpApp = this.pict.PictApplication;
		if (!tmpApp || (typeof tmpApp._platformDocsEditMode !== 'function') || !tmpApp._platformDocsEditMode()) { return ''; }
		const tmpEntries = this.provider.docHashesForSelection(pSelection);
		if (!tmpEntries.length) { return ''; }
		const tmpButtons = tmpEntries.map((pEntry) =>
			`<button type="button" class="sp-doc-editbtn" onclick="_Pict.PictApplication.editPlatformHelpDoc('${fAttr(pEntry.Hash)}'); return false;"><i class="fas fa-pen"></i> ${fEsc(pEntry.Tier)}</button>`).join('');
		return `<div class="sp-doc-editbar"><span class="sp-doc-editbar-label">Edit documentation:</span>${tmpButtons}</div>`;
	}

	/** Render markdown → HTML via the inline-doc content provider (falls back to the raw string if absent). */
	_parseMd(pMarkdown)
	{
		const tmpDocProvider = this.pict.providers['Pict-InlineDocumentation'];
		return (tmpDocProvider && tmpDocProvider._ContentProvider) ? tmpDocProvider._ContentProvider.parseMarkdown(pMarkdown) : pMarkdown;
	}

	/** Show HTML in the inline-documentation panel body (no-op if the host has no such panel). */
	_displayDocs(pHTML)
	{
		const tmpContentView = this.pict.views['InlineDoc-Content'];
		if (tmpContentView && (typeof tmpContentView.displayContent === 'function')) { tmpContentView.displayContent(pHTML); }
		// Flag the shared panel as showing OUR content, so the app restores its own docs the next time "?" or a
		// route change drives the panel (otherwise the sampling manual lingers on other pages).
		const tmpApp = this.pict.PictApplication;
		if (tmpApp) { tmpApp._helpShowingForeignDocs = true; }
	}

	/** Open/close the shared inline-documentation side panel, if the host exposes one. */
	_setHelpPanel(pOpen)
	{
		const tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.toggleHelp === 'function') { tmpApp.toggleHelp(!!pOpen); }
	}

	_detailHTML()
	{
		// An in-progress add takes over the panel (it is bound to _addTarget, not the tree selection).
		if (this._addMode === 'material') { return this._addMaterialHTML(); }
		if (this._addMode === 'entry') { return this._addEntryHTML(); }
		const tmpProvider = this.provider;
		const tmpSelection = tmpProvider ? tmpProvider.selected : null;
		if (!tmpSelection)
		{
			return '<div class="sp-d-empty">Select any row to see its detail, satisfaction, and documentation.</div>';
		}
		const tmpRecord = tmpSelection.Record || {};
		if (tmpSelection.Entity === 'Rule')
		{
			return (this._detailMode === 'edit') ? this._ruleEditHTML(tmpSelection) : this._ruleViewHTML(tmpSelection);
		}
		const tmpKindLabel = ({ Section: 'Pay-item section', LineItem: 'Line item', Material: 'Material' })[tmpSelection.Entity] || tmpSelection.Entity;
		const tmpTitle = (tmpSelection.Entity === 'Section') ? tmpRecord.TreeLabel : (tmpRecord.Name || tmpRecord.Title || '');
		// Add affordances: a Material can gain a new entry; a Line Item can gain a new material. Gated on edit rights.
		let tmpAddButton = '';
		if (tmpProvider.canEditRule())
		{
			if (tmpSelection.Entity === 'Material') { tmpAddButton = `<button type="button" class="sp-d-editbtn" onclick="_Pict.views.SamplePlan.enterAddEntry()"><i class="fas fa-plus"></i> Add entry</button>`; }
			else if (tmpSelection.Entity === 'LineItem') { tmpAddButton = `<button type="button" class="sp-d-editbtn" onclick="_Pict.views.SamplePlan.enterAddMaterial()"><i class="fas fa-plus"></i> Add material</button>`; }
		}
		const tmpFields = ((tmpSelection.Entity === 'Section') ? fField('Line items', tmpRecord.LineItemCount) : '')
			+ ((tmpSelection.Entity === 'LineItem') ? fField('Number', tmpRecord.Number) + fField('Description', tmpRecord.Description) + fField('Expected qty', `${tmpRecord.ExpectedQty} ${tmpRecord.Units || ''}`.trim()) : '')
			+ fField('Sample plan entries', tmpRecord.EntryCount)
			+ ((tmpRecord.SamplesRequired > 0) ? fField('Samples', `${tmpRecord.SamplesCreated} / ${tmpRecord.SamplesRequired}`) : '');
		return `<div class="sp-d-head"><div class="sp-d-headrow"><div class="sp-d-kind">${fEsc(tmpKindLabel)}</div>${tmpAddButton}</div><div class="sp-d-title">${fEsc(tmpTitle)}</div><div class="sp-d-meters">${fMeterHTML(this._meterFor(tmpSelection))}</div></div><div class="sp-d-grid">${tmpFields}</div>`;
	}

	// --- Rule VIEW mode: the full read-only field set (progressively enriched from the raw record) -------

	_ruleViewHTML(pSelection)
	{
		const tmpProvider = this.provider;
		const tmpRecord = pSelection.Record || {};
		const tmpRaw = tmpProvider.getRawRule(tmpRecord.IDRule);
		const tmpFeatures = tmpProvider.features;
		const tmpEditButton = tmpProvider.canEditRule()
			? `<button type="button" class="sp-d-editbtn" onclick="_Pict.views.SamplePlan.enterEdit()"><i class="fas fa-pen"></i> Edit</button>`
			: '';
		const tmpHead = `<div class="sp-d-head"><div class="sp-d-headrow"><div class="sp-d-kind">Sampling rule</div>${tmpEditButton}</div>`
			+ `<div class="sp-d-title">${fEsc(tmpRecord.Title)}</div><div class="sp-d-meters">${fMeterHTML(this._meterFor(pSelection))}</div></div>`;

		const tmpFreq = (tmpRecord.Kind === 'cert') ? 'By certificate'
			: (tmpRecord.Kind === 'perproject') ? '1 / project'
			: `1 / ${tmpRecord.Frequency} ${tmpRecord.Units || ''}`.trim();
		let tmpFields = fField('Sample type', tmpRecord.SampleType) + fField('Frequency', tmpFreq) + fField('Rate', tmpRecord.Rate) + fField('Units', tmpRecord.Units);
		if (tmpRaw)
		{
			tmpFields += fField('Conversion factor', tmpRaw.ConversionFactor) + fField('Sample size', tmpRaw.SampleSize) + fField('Sample units', tmpRaw.SampleUnits);
			if (tmpFeatures.ShowTestSelector) { tmpFields += fField('Test', tmpRecord.TestLabel || tmpRecord.TestHash) + fField('Intent', tmpRaw.Intent); }
		}
		// count → sampling progress; per-project test → the samples actually taken; certificate → the acceptance basis.
		if (tmpRecord.Kind === 'count') { tmpFields += fField('Required (est.)', tmpRecord.Required) + fField('Created', tmpRecord.Created) + fField('Passing', tmpRecord.Passing); }
		else if (tmpRecord.Kind === 'perproject') { tmpFields += fField('Created', tmpRecord.Created) + fField('Passing', tmpRecord.Passing); }
		else { tmpFields += fField('Acceptance', 'By certificate'); }

		let tmpWide = '';
		if (tmpRaw)
		{
			tmpWide = fFieldWide('Sample description', tmpRaw.SampleDescription) + fFieldWide('Sampling instructions', tmpRaw.SamplingInstructions)
				+ fFieldWide('Test description', tmpRaw.TestDescription) + fFieldWide('Remarks', tmpRaw.Remarks)
				+ ((Number(tmpRaw.ErrorOmission) === 1) ? fFieldWide('Error / omission', tmpRaw.ErrorOmissionDetail || 'Flagged') : '');
		}
		else if (this._detailFetchFailed)
		{
			tmpWide = '<div class="sp-d-loadingnote sp-d-loadfail"><i class="fas fa-triangle-exclamation"></i> Could not load the full detail. <a href="#" onclick="_Pict.views.SamplePlan.retryDetailLoad(); return false;">Retry</a></div>';
		}
		else
		{
			tmpWide = '<div class="sp-d-loadingnote"><i class="fas fa-spinner fa-spin"></i> Loading full detail…</div>';
		}
		// A failed enterEdit leaves an error but stays in VIEW — surface it here (mirrors the edit form's banner).
		const tmpErrorBanner = this._editError ? `<div class="sp-d-error"><i class="fas fa-triangle-exclamation"></i> ${fEsc(this._editError)}</div>` : '';
		// Documentation lives in the inline-documentation side panel (see _syncDocsPanel), not in the record.
		return tmpHead + tmpErrorBanner + `<div class="sp-d-grid">${tmpFields}</div>${tmpWide}`;
	}

	// --- Rule EDIT mode: the editable form (dropdowns where the legacy editor has them) -----------------

	/** The shared editable field body (used by both the edit form and the add-entry form), bound to the given buffer. */
	_ruleFormFieldsHTML(pBuffer)
	{
		const tmpProvider = this.provider;
		const tmpFeatures = tmpProvider.features;
		// Rate: a 0/1 toggle when the customer's config enables it, else a numeric input (matching the legacy editor).
		const tmpRateHTML = tmpFeatures.ShowRateToggle
			? `<div class="sp-d-editrow"><label class="sp-d-flabel">Rate</label><label class="sp-d-toggle"><span>0</span>`
				+ `<input type="checkbox"${(Number(pBuffer.Rate) === 1) ? ' checked' : ''} onchange="_Pict.views.SamplePlan.onRateToggle(this.checked)" /><span>1</span></label></div>`
			: fInput('Rate', 'Rate', pBuffer.Rate, 'number');

		const tmpUnitsOptions = this._withCurrent(tmpProvider.unitsOptions, pBuffer.Units).map((pUnit) => ({ value: pUnit, label: pUnit }));
		if (pBuffer.Units == null || (pBuffer.Units === '')) { tmpUnitsOptions.unshift({ value: '', label: '— none —' }); }
		const tmpCounts = tmpProvider.serverCountsFor(pBuffer);
		const tmpPlannedHTML = this._overrideRowHTML('Planned Samples', 'Planned', (Number(pBuffer.ManualPlannedSampleCount) > -1), pBuffer.ManualPlannedSampleCount, tmpCounts.ComputedPlannedSampleCount, 'sp-ed-plan-val');
		const tmpRequiredHTML = this._overrideRowHTML('Required Samples', 'Required', (Number(pBuffer.ManualRequiredSampleCount) > -1), pBuffer.ManualRequiredSampleCount, tmpCounts.ComputedRequiredSampleCount, 'sp-ed-req-val');

		let tmpTestHTML = '';
		if (tmpFeatures.ShowTestSelector)
		{
			const tmpTestOptions = tmpProvider.testOptionsFor(pBuffer.IDTest).map((pOption) => ({ value: pOption.IDTest, label: pOption.Name }));
			tmpTestHTML = fSelect('Test', 'IDTest', (pBuffer.IDTest == null ? 0 : pBuffer.IDTest), tmpTestOptions)
				+ fCombo('Intent', 'Intent', pBuffer.Intent, tmpProvider.intentOptions, 'sp-ed-intent-list');
		}

		const tmpErrorOmission = (Number(pBuffer.ErrorOmission) === 1);
		const tmpShowDetail = tmpErrorOmission || (pBuffer.ErrorOmissionDetail && (pBuffer.ErrorOmissionDetail !== ''));
		const tmpEOHTML = `<div class="sp-d-editrow sp-d-field-wide"><label class="sp-d-checkline"><input type="checkbox"${tmpErrorOmission ? ' checked' : ''} onchange="_Pict.views.SamplePlan.onToggleErrorOmission(this.checked)" /> Error / Omission</label>`
			+ (tmpShowDetail ? `<textarea class="sp-d-textarea" rows="2" placeholder="Enter Error/Omission details"${tmpErrorOmission ? '' : ' disabled'} oninput="_Pict.views.SamplePlan.onEditField('ErrorOmissionDetail', this.value)">${fEsc(pBuffer.ErrorOmissionDetail || '')}</textarea>` : '')
			+ `</div>`;

		return tmpRateHTML
			+ fInput('Frequency', 'Frequency', pBuffer.Frequency, 'number')
			+ fInput('Conversion Factor', 'ConversionFactor', pBuffer.ConversionFactor, 'number', '0.00001')
			+ fSelect('Units', 'Units', pBuffer.Units, tmpUnitsOptions)
			+ tmpPlannedHTML
			+ tmpRequiredHTML
			+ fTextarea('Sample Description', 'SampleDescription', pBuffer.SampleDescription)
			+ fTextarea('Sampling Instructions', 'SamplingInstructions', pBuffer.SamplingInstructions)
			+ fInput('Sample Size', 'SampleSize', pBuffer.SampleSize, 'number', '1')
			+ fCombo('Sample Units', 'SampleUnits', pBuffer.SampleUnits, tmpProvider.unitsOptions, 'sp-ed-units-list')
			+ tmpTestHTML
			+ fTextarea('Test Description', 'TestDescription', pBuffer.TestDescription)
			+ fTextarea('Remarks', 'Remarks', pBuffer.Remarks)
			+ tmpEOHTML;
	}

	_ruleEditHTML(pSelection)
	{
		const tmpBuffer = this._editBuffer || {};
		const tmpRecord = pSelection.Record || {};
		const tmpTitle = tmpBuffer.TestDescription || tmpBuffer.SampleDescription || tmpRecord.Title || 'Material Acceptance';
		const tmpSampleType = tmpBuffer.SampleType || tmpRecord.SampleType || '';
		const tmpErrorBanner = this._editError ? `<div class="sp-d-error"><i class="fas fa-triangle-exclamation"></i> ${fEsc(this._editError)}</div>` : '';
		const tmpBusy = this._editSaving ? ' disabled' : '';
		const tmpActions = `<div class="sp-d-actions">`
			+ `<button type="button" class="sp-d-btn sp-d-btn-primary" onclick="_Pict.views.SamplePlan.saveDetail()"${tmpBusy}>${this._editSaving ? 'Saving…' : 'Save'}</button>`
			+ `<button type="button" class="sp-d-btn" onclick="_Pict.views.SamplePlan.cancelDetail()"${tmpBusy}>Cancel</button></div>`;
		return `<div class="sp-d-head"><div class="sp-d-headrow"><div class="sp-d-kind">Sampling rule · editing</div></div>`
			+ `<div class="sp-d-title">${fEsc(tmpTitle)}</div><div class="sp-d-subkind">Sample Type: ${fEsc(tmpSampleType)}</div></div>`
			+ tmpErrorBanner
			+ `<div class="sp-d-form">${this._ruleFormFieldsHTML(tmpBuffer)}</div>`
			+ tmpActions;
	}

	/** One Planned/Required row: an Override checkbox plus either a manual number input or the live computed readout. */
	_overrideRowHTML(pLabel, pWhich, pOverride, pManualValue, pComputed, pReadoutID)
	{
		const tmpField = (pWhich === 'Planned') ? 'ManualPlannedSampleCount' : 'ManualRequiredSampleCount';
		const tmpCheckbox = `<label class="sp-d-checkinline"><input type="checkbox"${pOverride ? ' checked' : ''} onchange="_Pict.views.SamplePlan.onToggleOverride('${pWhich}', this.checked)" /> Override</label>`;
		const tmpBody = pOverride
			? `<input class="sp-d-input" type="number" step="1" value="${fAttr(pManualValue)}" oninput="_Pict.views.SamplePlan.onEditField('${tmpField}', this.value)" />`
			: `<div class="sp-d-computed">Computed: <span id="${pReadoutID}">${fEsc(pComputed)}</span></div>`;
		return `<div class="sp-d-editrow"><div class="sp-d-overhead"><label class="sp-d-flabel">${fEsc(pLabel)}</label>${tmpCheckbox}</div>${tmpBody}</div>`;
	}

	/** Keep an existing value selectable even when it isn't in the derived vocabulary (so nothing renders blank). */
	_withCurrent(pOptions, pValue)
	{
		const tmpList = (pOptions || []).slice();
		const tmpValue = (pValue == null) ? '' : String(pValue);
		if ((tmpValue !== '') && (tmpList.indexOf(tmpValue) === -1)) { tmpList.unshift(tmpValue); }
		return tmpList;
	}

	// --- Edit handlers ----------------------------------------------------------------------------------

	/** Enter edit mode: fetch the full rule record (+ the Test catalog when needed), buffer it, then paint the form. */
	enterEdit()
	{
		const tmpProvider = this.provider;
		const tmpSelection = tmpProvider ? tmpProvider.selected : null;
		if (!tmpSelection || (tmpSelection.Entity !== 'Rule') || !tmpProvider.canEditRule()) { return; }
		this._editError = '';
		this._editSaving = false;
		Promise.all(
		[
			tmpProvider.fetchRuleRecordAsync(tmpSelection),
			tmpProvider.features.ShowTestSelector ? tmpProvider.ensureTestOptionsAsync() : Promise.resolve(null),
		])
			.then((pResults) =>
			{
				if (!tmpProvider.selected || (tmpProvider.selected.Key !== tmpSelection.Key)) { return; }   // selection moved on
				const tmpRaw = pResults[0];
				if (!tmpRaw) { this._editError = 'Could not load the full rule record to edit.'; this.renderDetail(); return; }
				this._editBuffer = this._normalizeBuffer(tmpRaw);
				this._detailMode = 'edit';
				this.renderDetail();
			})
			.catch((pError) =>
			{
				this._editError = (pError && pError.message) ? pError.message : 'Could not load the rule record.';
				this.renderDetail();
			});
	}

	/** Clone the raw record into the edit buffer and coerce the numeric/flag columns so the form binds cleanly. */
	_normalizeBuffer(pRaw)
	{
		const tmpBuffer = JSON.parse(JSON.stringify(pRaw));
		const fNum = (pValue, pDefault) => { const tmp = Number(pValue); return Number.isFinite(tmp) ? tmp : pDefault; };
		tmpBuffer.Rate = fNum(tmpBuffer.Rate, 1);
		tmpBuffer.Frequency = fNum(tmpBuffer.Frequency, 0);
		tmpBuffer.ConversionFactor = fNum(tmpBuffer.ConversionFactor, 1);
		tmpBuffer.SampleSize = fNum(tmpBuffer.SampleSize, 0);
		tmpBuffer.ManualPlannedSampleCount = fNum(tmpBuffer.ManualPlannedSampleCount, -1);
		tmpBuffer.ManualRequiredSampleCount = fNum(tmpBuffer.ManualRequiredSampleCount, -1);
		tmpBuffer.ErrorOmission = (Number(tmpBuffer.ErrorOmission) === 1) ? 1 : 0;
		tmpBuffer.IDTest = (tmpBuffer.IDTest == null) ? 0 : fNum(tmpBuffer.IDTest, 0);
		return tmpBuffer;
	}

	/** The buffer the form is currently bound to — the add draft when adding, else the edit buffer. */
	_activeBuffer() { return this._addBuffer || this._editBuffer; }

	/** A field changed: write it to the active buffer (coercing numerics) and refresh the live computed counts when relevant. */
	onEditField(pField, pValue)
	{
		const tmpBuffer = this._activeBuffer();
		if (!tmpBuffer) { return; }
		const tmpNumericFields = { Rate: 1, Frequency: 1, ConversionFactor: 1, SampleSize: 1, ManualPlannedSampleCount: 1, ManualRequiredSampleCount: 1, IDTest: 1 };
		// Integer columns — round so a pasted/typed decimal never reaches an int column (the DB would silently truncate).
		const tmpIntegerFields = { SampleSize: 1, ManualPlannedSampleCount: 1, ManualRequiredSampleCount: 1, IDTest: 1 };
		if (tmpNumericFields[pField])
		{
			const tmpNum = Number(pValue);
			tmpBuffer[pField] = Number.isFinite(tmpNum) ? (tmpIntegerFields[pField] ? Math.round(tmpNum) : tmpNum) : 0;
		}
		else
		{
			tmpBuffer[pField] = pValue;
		}
		if ((pField === 'Rate') || (pField === 'Frequency') || (pField === 'ConversionFactor') || (pField === 'Units')) { this._updateComputedReadouts(); }
	}

	/**
	 * A count-affecting field (Rate/Frequency/Conversion/Units) changed. The required/planned counts are computed by
	 * the SERVER (never client-side), so they can't update live — mark the readouts as pending; they refresh from the
	 * endpoint when the rule is saved. (The readouts are only present while their manual override is off.)
	 */
	_updateComputedReadouts()
	{
		const tmpBuffer = this._activeBuffer();
		if (!tmpBuffer) { return; }
		const tmpPlanned = document.getElementById('sp-ed-plan-val');
		const tmpRequired = document.getElementById('sp-ed-req-val');
		const fPending = (pEl) => { if (pEl) { pEl.textContent = 'recalculates on save'; pEl.title = 'Required and planned counts are computed by the server; they update when you save.'; pEl.classList.add('sp-ed-pending'); } };
		fPending(tmpPlanned);
		fPending(tmpRequired);
	}

	/** The Rate 0/1 toggle. */
	onRateToggle(pChecked)
	{
		const tmpBuffer = this._activeBuffer();
		if (!tmpBuffer) { return; }
		tmpBuffer.Rate = pChecked ? 1 : 0;
		this._updateComputedReadouts();
	}

	/** Toggle a Planned/Required override: seed the manual value with the current computed count (no legacy -1 in the box). */
	onToggleOverride(pWhich, pChecked)
	{
		const tmpBuffer = this._activeBuffer();
		if (!tmpBuffer) { return; }
		const tmpField = (pWhich === 'Planned') ? 'ManualPlannedSampleCount' : 'ManualRequiredSampleCount';
		if (pChecked)
		{
			const tmpCounts = this.provider.serverCountsFor(tmpBuffer);
			tmpBuffer[tmpField] = (pWhich === 'Planned') ? Number(tmpCounts.ComputedPlannedSampleCount) : Number(tmpCounts.ComputedRequiredSampleCount);
		}
		else
		{
			tmpBuffer[tmpField] = -1;
		}
		this.renderDetail();
	}

	/** Toggle the Error/Omission flag (shows/enables the detail field). Keeps any typed detail rather than dropping it. */
	onToggleErrorOmission(pChecked)
	{
		const tmpBuffer = this._activeBuffer();
		if (!tmpBuffer) { return; }
		tmpBuffer.ErrorOmission = pChecked ? 1 : 0;
		this.renderDetail();
	}

	/** Save the buffered edits through the provider, then return to VIEW (or surface the server error). */
	saveDetail()
	{
		if (!this._editBuffer || this._editSaving) { return; }
		this._editSaving = true;
		this._editError = '';
		this.renderDetail();
		this.provider.saveRuleAsync(this._editBuffer).then((pResult) =>
		{
			this._editSaving = false;
			if (pResult && pResult.ok)
			{
				this._detailMode = 'view';
				this._editBuffer = null;
				this._editError = '';
				this.renderDetail();
				this._refreshHeader();
				return;
			}
			this._editError = (pResult && pResult.error) ? String(pResult.error) : 'The save failed.';
			this.renderDetail();
		}).catch((pError) =>
		{
			this._editSaving = false;
			this._editError = (pError && pError.message) ? pError.message : 'The save failed.';
			this.renderDetail();
		});
	}

	/** Discard the buffered edits and return to VIEW. */
	cancelDetail()
	{
		this._detailMode = 'view';
		this._editBuffer = null;
		this._editError = '';
		this.renderDetail();
	}

	// --- Add: a new entry under a material, or a new material on a line item -----------------------------

	/** Refresh just one already-loaded tree folder in place (its resolver re-reads the mutable index) — no collapse. */
	_refreshTreeFolders(pLabel, pMatchFn)
	{
		const tmpExplorer = this._explorer;
		if (!tmpExplorer || (typeof tmpExplorer._state !== 'function') || (typeof tmpExplorer._reloadFolderMembers !== 'function')) { return; }
		const tmpNodes = (tmpExplorer._state() || {}).Nodes || {};
		Object.keys(tmpNodes).forEach((pKey) =>
		{
			const tmpNode = tmpNodes[pKey];
			if (tmpNode && (tmpNode.Kind === 'folder') && (tmpNode.Label === pLabel) && tmpNode.Loaded && pMatchFn(tmpNode)) { tmpExplorer._reloadFolderMembers(pKey, tmpNode); }
		});
	}

	/** "+ Add entry" on a selected Material. */
	enterAddEntry()
	{
		const tmpProvider = this.provider;
		const tmpSelection = tmpProvider ? tmpProvider.selected : null;
		if (!tmpSelection || (tmpSelection.Entity !== 'Material') || !tmpProvider.canEditRule()) { return; }
		this._beginAddEntry(tmpSelection.Record);
	}

	/** Start an add-entry draft for a material node (used by the Material button and right after adding a material). */
	_beginAddEntry(pMaterial)
	{
		const tmpProvider = this.provider;
		this._addError = '';
		this._addSaving = false;
		// Guard the async vocab load: if the user cancels or moves on before it resolves, this stale callback must not
		// revive the add form for the wrong material (cancelAdd / a selection change bump _addEntryToken).
		const tmpToken = (this._addEntryToken = (this._addEntryToken || 0) + 1);
		const fShow = () =>
		{
			if (tmpToken !== this._addEntryToken) { return; }
			this._addTarget = pMaterial;
			this._addBuffer = this._newEntryBuffer(pMaterial);
			this._addMode = 'entry';
			this.renderDetail();
		};
		Promise.all(
		[
			tmpProvider.ensureSampleTypesAsync(),
			tmpProvider.features.ShowTestSelector ? tmpProvider.ensureTestOptionsAsync() : Promise.resolve(null),
		]).then(fShow).catch(fShow);
	}

	/** A sensible default new-entry draft under a material. */
	_newEntryBuffer(pMaterial)
	{
		const tmpProvider = this.provider;
		const tmpDefaultSampleType = (tmpProvider.sampleTypeOptions()[0] && tmpProvider.sampleTypeOptions()[0].value) || 'ACPT';
		return {
			IDLineItem: pMaterial.IDLineItem,
			IDMaterial: pMaterial.IDMaterial,
			SampleType: tmpDefaultSampleType,
			IDTest: 0,
			Rate: 1,
			Frequency: 1,
			ConversionFactor: 1,
			Units: tmpProvider.defaultUnitsForMaterial(pMaterial.IDLineItem, pMaterial.IDMaterial),
			SampleSize: 0,
			SampleUnits: '',
			SampleDescription: '',
			SamplingInstructions: '',
			TestDescription: '',
			Remarks: '',
			Intent: '',
			ErrorOmission: 0,
			ErrorOmissionDetail: '',
			ManualPlannedSampleCount: -1,
			ManualRequiredSampleCount: -1,
		};
	}

	/** The add-entry form: a Sample Type dropdown + the shared rule fields, with Create / Cancel. */
	_addEntryHTML()
	{
		const tmpProvider = this.provider;
		const tmpBuffer = this._addBuffer || {};
		const tmpMaterial = this._addTarget || {};
		const tmpOptions = tmpProvider.sampleTypeOptions();
		const tmpSampleTypeOptions = tmpOptions.length ? tmpOptions : [ { value: tmpBuffer.SampleType || 'ACPT', label: tmpBuffer.SampleType || 'ACPT' } ];
		const tmpErrorBanner = this._addError ? `<div class="sp-d-error"><i class="fas fa-triangle-exclamation"></i> ${fEsc(this._addError)}</div>` : '';
		const tmpBusy = this._addSaving ? ' disabled' : '';
		const tmpActions = `<div class="sp-d-actions">`
			+ `<button type="button" class="sp-d-btn sp-d-btn-primary" onclick="_Pict.views.SamplePlan.createEntry()"${tmpBusy}>${this._addSaving ? 'Adding…' : 'Create entry'}</button>`
			+ `<button type="button" class="sp-d-btn" onclick="_Pict.views.SamplePlan.cancelAdd()"${tmpBusy}>Cancel</button></div>`;
		return `<div class="sp-d-head"><div class="sp-d-headrow"><div class="sp-d-kind">New sample plan entry</div></div>`
			+ `<div class="sp-d-title">${fEsc(tmpMaterial.Name || 'Material')}</div><div class="sp-d-subkind">Adding a test entry to this material</div></div>`
			+ tmpErrorBanner
			+ `<div class="sp-d-form">${fSelect('Sample Type', 'SampleType', tmpBuffer.SampleType, tmpSampleTypeOptions)}${this._ruleFormFieldsHTML(tmpBuffer)}</div>`
			+ tmpActions;
	}

	/** Create the drafted entry through the provider, then show it and refresh its material's Tests folder in place. */
	createEntry()
	{
		if (!this._addBuffer || this._addSaving) { return; }
		this._addSaving = true;
		this._addError = '';
		this.renderDetail();
		this.provider.createEntryAsync(this._addBuffer).then((pResult) =>
		{
			this._addSaving = false;
			if (pResult && pResult.ok)
			{
				const tmpRule = pResult.rule;
				this._addMode = null;
				this._addBuffer = null;
				this._addTarget = null;
				this._addError = '';
				this._refreshTreeFolders('Tests', (pNode) => pNode.ParentRecord && (String(pNode.ParentRecord.Key) === String(tmpRule.MaterialKey)));
				// Land on the new entry (VIEW), forcing a fresh selection so its detail loads.
				this.provider.selectRuleRecord(tmpRule);
				this._detailKey = null;
				this.renderDetail();
				this._refreshHeader();
				return;
			}
			this._addError = (pResult && pResult.error) ? String(pResult.error) : 'Could not create the entry.';
			this.renderDetail();
		}).catch((pError) =>
		{
			this._addSaving = false;
			this._addError = (pError && pError.message) ? pError.message : 'Could not create the entry.';
			this.renderDetail();
		});
	}

	/** "+ Add material" on a selected Line Item → the material picker. */
	enterAddMaterial()
	{
		const tmpProvider = this.provider;
		const tmpSelection = tmpProvider ? tmpProvider.selected : null;
		if (!tmpSelection || (tmpSelection.Entity !== 'LineItem') || !tmpProvider.canEditRule()) { return; }
		this._addTarget = tmpSelection.Record;
		this._addMode = 'material';
		this._addError = '';
		this._materialLoadToken = 0;
		this.renderDetail();
		this._loadMaterials('');
	}

	/** The material picker: a search box + a filterable list of catalog materials not already on this line item. */
	_addMaterialHTML()
	{
		const tmpLineItem = this._addTarget || {};
		const tmpErrorBanner = this._addError ? `<div class="sp-d-error"><i class="fas fa-triangle-exclamation"></i> ${fEsc(this._addError)}</div>` : '';
		return `<div class="sp-d-head"><div class="sp-d-headrow"><div class="sp-d-kind">Add material</div></div>`
			+ `<div class="sp-d-title">${fEsc(tmpLineItem.Name || 'Line item')}</div><div class="sp-d-subkind">Attach a material to this line item</div></div>`
			+ tmpErrorBanner
			+ `<input id="SamplePlan-MaterialFilter" class="sp-proj-filter" type="text" autocomplete="off" placeholder="Search materials by name or code…" oninput="_Pict.views.SamplePlan.onMaterialFilter(this.value)" />`
			+ `<div id="SamplePlan-MaterialList" class="sp-proj-list"><div class="sp-proj-note"><i class="fas fa-spinner fa-spin"></i> Loading materials…</div></div>`
			+ `<div class="sp-d-actions"><button type="button" class="sp-d-btn" onclick="_Pict.views.SamplePlan.cancelAdd()">Cancel</button></div>`;
	}

	/** Debounced material search (into #SamplePlan-MaterialList). */
	onMaterialFilter(pValue)
	{
		if (this._materialFilterTimer) { clearTimeout(this._materialFilterTimer); }
		this._materialFilterTimer = setTimeout(() => { this._loadMaterials(pValue); }, 200);
	}

	_loadMaterials(pTerm)
	{
		const tmpProvider = this.provider;
		const tmpTarget = this._addTarget;
		if (!tmpProvider || !tmpTarget) { return; }
		const tmpToken = (this._materialLoadToken = (this._materialLoadToken || 0) + 1);
		tmpProvider.searchMaterialsAsync(pTerm, tmpTarget.IDLineItem).then((pMaterials) =>
		{
			if (tmpToken === this._materialLoadToken) { this._renderMaterialList(pMaterials, pTerm); }
		}).catch(() =>
		{
			if (tmpToken === this._materialLoadToken) { this.pict.ContentAssignment.assignContent('#SamplePlan-MaterialList', '<div class="sp-proj-note sp-proj-err">Could not load materials.</div>'); }
		});
	}

	_renderMaterialList(pMaterials, pTerm)
	{
		const tmpMount = this.pict.ContentAssignment.getElement('#SamplePlan-MaterialList');
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		const tmpList = Array.isArray(pMaterials) ? pMaterials : [];
		this._materialResults = tmpList;   // so addMaterialPick can recover the chosen record (with UnitsImperial)
		if (!tmpList.length)
		{
			const tmpTerm = String(pTerm == null ? '' : pTerm).trim();
			this.pict.ContentAssignment.assignContent('#SamplePlan-MaterialList', `<div class="sp-proj-note">${tmpTerm ? `No materials match “${fEsc(tmpTerm)}”.` : 'No materials available.'}</div>`);
			return;
		}
		const tmpRows = tmpList.map((pMaterial) =>
		{
			const tmpCode = pMaterial.MaterialCode ? fEsc(pMaterial.MaterialCode) : '';
			return `<button type="button" class="sp-proj-row" onclick="_Pict.views.SamplePlan.addMaterialPick(${Number(pMaterial.IDMaterial)})">`
				+ `<span class="sp-proj-name">${fEsc(pMaterial.Name)}</span><span class="sp-proj-year">${tmpCode}</span>`
				+ `<span class="sp-proj-go"><i class="fas fa-plus"></i></span></button>`;
		}).join('');
		const tmpCapNote = (tmpList.length >= 100) ? '<div class="sp-proj-note sp-proj-cap">Showing the first 100 — narrow with search.</div>' : '';
		this.pict.ContentAssignment.assignContent('#SamplePlan-MaterialList', tmpRows + tmpCapNote);
	}

	/** Pick a material → create its join, refresh the tree, then flow into adding its first entry. */
	addMaterialPick(pIDMaterial)
	{
		const tmpProvider = this.provider;
		const tmpLineItem = this._addTarget;
		if (!tmpProvider || !tmpLineItem) { return; }
		const tmpMaterial = (this._materialResults || []).find((pMaterial) => Number(pMaterial.IDMaterial) === Number(pIDMaterial));
		if (!tmpMaterial) { return; }
		this._addError = '';
		this.pict.ContentAssignment.assignContent('#SamplePlan-MaterialList', '<div class="sp-proj-note"><i class="fas fa-spinner fa-spin"></i> Adding material…</div>');
		tmpProvider.addMaterialAsync(tmpLineItem, tmpMaterial).then((pResult) =>
		{
			if (pResult && pResult.ok)
			{
				const tmpLineItemID = tmpLineItem.IDLineItem;
				this._refreshTreeFolders('Materials', (pNode) => pNode.ParentRecord && (Number(pNode.ParentRecord.IDLineItem) === Number(tmpLineItemID)));
				this._refreshHeader();
				this._beginAddEntry(pResult.material);   // continue inline into the first entry
				return;
			}
			this._addError = (pResult && pResult.error) ? String(pResult.error) : 'Could not add the material.';
			this.renderDetail();
			this._loadMaterials('');
		}).catch((pError) =>
		{
			this._addError = (pError && pError.message) ? pError.message : 'Could not add the material.';
			this.renderDetail();
			this._loadMaterials('');
		});
	}

	/** Abandon the add flow and return to the selected node's detail. */
	cancelAdd()
	{
		this._addMode = null;
		this._addBuffer = null;
		this._addTarget = null;
		this._addError = '';
		this._addSaving = false;
		this._addEntryToken = (this._addEntryToken || 0) + 1;   // invalidate any in-flight add-entry vocab load
		this.renderDetail();
	}

	// Re-derive the meter strip for the detail header (same shapes the tree rows use).
	_meterFor(pSelection)
	{
		const tmpRecord = pSelection.Record || {};
		const fBarTone = (pPercent) => ((pPercent >= 90) ? 'ok' : ((pPercent >= 50) ? 'warn' : 'bad'));
		if (pSelection.Entity === 'Rule')
		{
			if (tmpRecord.Kind === 'cert') { return [ { Kind: 'pill', Tone: 'muted', Text: 'by certificate' } ]; }
			if (tmpRecord.Kind === 'perproject') { return [ { Kind: 'pill', Tone: 'muted', Text: '1 / project' } ]; }
			const tmpPct = (tmpRecord.Required > 0) ? Math.round((tmpRecord.Created / tmpRecord.Required) * 100) : 0;
			return [ { Kind: 'bar', Percent: tmpPct, Tone: fBarTone(tmpPct), Label: `${tmpRecord.Created}/${tmpRecord.Required}` } ];
		}
		const tmpMeters = [];
		if (tmpRecord.SamplesRequired > 0) { const tmpPct = Math.round((tmpRecord.SamplesCreated / tmpRecord.SamplesRequired) * 100); tmpMeters.push({ Kind: 'bar', Percent: tmpPct, Tone: fBarTone(tmpPct), Label: `${tmpRecord.SamplesCreated}/${tmpRecord.SamplesRequired}` }); }
		if (tmpRecord.EntryCount > 0) { tmpMeters.push({ Kind: 'pill', Tone: 'muted', Text: `${tmpRecord.EntryCount} ${tmpRecord.EntryCount === 1 ? 'entry' : 'entries'}` }); }
		return tmpMeters;
	}
}

module.exports = SamplePlan;

module.exports.default_configuration =
{
	ViewIdentifier: 'SamplePlan',
	DefaultRenderable: 'SamplePlanWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
.sampleplan-head { display: flex; align-items: center; gap: 0.9rem; max-width: 1180px; margin: 0 auto 1rem; }
.sampleplan-ic { display: inline-flex; align-items: center; justify-content: center; width: 2.6rem; height: 2.6rem; border-radius: 12px;
	background: var(--theme-color-background-tertiary, #eef1f5); color: var(--theme-color-brand-primary, #156dd1); font-size: 1.2rem; flex: 0 0 auto; }
.sampleplan-title { font-size: 1.5rem; font-weight: 700; line-height: 1.1; color: var(--theme-color-text-primary, #1f2733); margin: 0; }
.sampleplan-sub { font-size: 0.92rem; color: var(--theme-color-text-muted, #6b7686); margin: 0.15rem 0 0; }
.sampleplan-docs { margin-left: auto; display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.9rem; flex: 0 0 auto; cursor: pointer; padding: 0.35rem 0.75rem; border: 1px solid var(--theme-color-border, #d6dbe3); border-radius: 6px; background: var(--theme-color-background, #fff); color: var(--theme-color-text-secondary, #5a6573); }
.sampleplan-docs:hover { background: var(--theme-color-background-secondary, #f4f6f9); color: var(--theme-color-text-primary, #1f2733); }
.sp-docnav { margin-bottom: 0.8rem; font-size: 0.85rem; }
.sp-docnav a, .sp-docidx a { cursor: pointer; color: var(--theme-color-brand-primary, #2E7D74); text-decoration: none; }
.sp-docnav a:hover, .sp-docidx a:hover { text-decoration: underline; }
.sp-docidx h1 { font-size: 1.1rem; margin: 0 0 0.6rem; }
.sp-docidx h2 { font-size: 0.95rem; margin: 1rem 0 0.4rem; color: var(--theme-color-text-secondary, #5a6573); }
.sp-docidx ul { margin: 0; padding-left: 1.1rem; }
.sp-docidx li { margin: 0.2rem 0; }
.sp-docidx-loading { color: var(--theme-color-text-muted, #9aa3b1); font-size: 0.9rem; padding: 0.5rem 0; }
.sampleplan-wrap { max-width: 1180px; margin: 0 auto; }
.sp-split { display: flex; gap: 1rem; align-items: flex-start; }
.sp-tree { flex: 1 1 58%; min-width: 0; overflow-x: auto; background: var(--theme-color-background-primary, #fff);
	border: 1px solid var(--theme-color-border-light, #eef1f5); border-radius: 12px; padding: 0.7rem 0.8rem; min-height: 60vh; }
.sp-tree .pdex-title { min-width: 5rem; }
.sp-detail { flex: 1 1 42%; min-width: 0; position: sticky; top: 1rem; background: var(--theme-color-background-primary, #fff);
	border: 1px solid var(--theme-color-border-light, #eef1f5); border-radius: 12px; padding: 1rem 1.1rem; max-height: 82vh; overflow: auto; }
.sp-d-empty { color: var(--theme-color-text-muted, #8a94a3); font-size: 0.9rem; padding: 2.5rem 1rem; text-align: center; }
.sp-d-head { border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); padding-bottom: 0.6rem; margin-bottom: 0.75rem; }
.sp-d-kind { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted, #8a94a3); }
.sp-d-title { font-size: 1.05rem; font-weight: 600; margin: 0.1rem 0 0.55rem; line-height: 1.3; }
.sp-d-meters { display: flex; gap: 0.45rem; align-items: center; flex-wrap: wrap; }
.sp-d-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; margin-bottom: 1rem; }
.sp-d-field { background: var(--theme-color-background-tertiary, #f6f8fb); border-radius: 8px; padding: 0.4rem 0.6rem; }
.sp-d-flabel { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-text-muted, #8a94a3); }
.sp-d-fval { font-size: 0.9rem; font-weight: 500; margin-top: 0.05rem; }
.sp-d-docs-h { font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5a6573); margin-bottom: 0.5rem; }
.sp-d-docs-src { font-weight: 400; color: var(--theme-color-text-muted, #9aa3b1); text-transform: none; letter-spacing: 0; }
.sp-d-doc { border: 1px solid var(--theme-color-border-light, #eef1f5); border-left: 3px solid var(--theme-color-brand-primary, #156dd1); border-radius: 8px; padding: 0.45rem 0.6rem; margin-bottom: 0.45rem; }
.sp-d-dochead { display: flex; justify-content: space-between; align-items: baseline; gap: 0.5rem; margin-bottom: 0.2rem; }
.sp-d-doclabel { font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5a6573); }
.sp-d-dochash { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.66rem; color: var(--theme-color-text-muted, #aab2bf); }
.sp-d-docbody { font-size: 0.85rem; line-height: 1.5; white-space: pre-wrap; }
.sp-d-docempty { color: var(--theme-color-text-muted, #aab2bf); font-style: italic; }
.sampleplan-picker { max-width: 640px; margin: 1.5rem auto; }
.sp-proj-h { font-size: 1.15rem; font-weight: 700; color: var(--theme-color-text-primary, #1f2733); margin: 0 0 0.2rem; }
.sp-proj-sub { font-size: 0.9rem; color: var(--theme-color-text-muted, #6b7686); margin: 0 0 0.9rem; }
.sp-proj-filter { width: 100%; box-sizing: border-box; font-size: 0.95rem; padding: 0.55rem 0.75rem; margin: 0 0 0.7rem;
	border: 1px solid var(--theme-color-border, #d6dbe3); border-radius: 8px; background: var(--theme-color-background-input, #fff); color: var(--theme-color-text-primary, #1f2733); }
.sp-proj-filter:focus { outline: none; border-color: var(--theme-color-brand-primary, #156dd1); box-shadow: 0 0 0 2px var(--theme-color-brand-primary-faded, rgba(21, 109, 209, 0.18)); }
.sp-proj-list { border: 1px solid var(--theme-color-border-light, #eef1f5); border-radius: 10px; overflow: hidden; background: var(--theme-color-background-primary, #fff); }
.sp-proj-row { display: flex; align-items: center; gap: 0.6rem; width: 100%; text-align: left; box-sizing: border-box; cursor: pointer;
	padding: 0.6rem 0.85rem; border: none; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); background: transparent; color: var(--theme-color-text-primary, #1f2733); font-size: 0.92rem; }
.sp-proj-row:last-child { border-bottom: none; }
.sp-proj-row:hover { background: var(--theme-color-background-secondary, #f4f6f9); }
.sp-proj-name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sp-proj-head { display: flex; align-items: center; gap: 0.6rem; padding: 0.4rem 0.85rem; font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted, #8a94a3); background: var(--theme-color-background-secondary, #f4f6f9); border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); }
.sp-proj-year { flex: 0 0 5.5rem; text-align: right; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.82rem; color: var(--theme-color-text-secondary, #5a6573); }
.sp-proj-head .sp-proj-year { font-family: inherit; }
.sp-proj-go { flex: 0 0 1rem; text-align: center; color: var(--theme-color-text-muted, #b3bcc9); font-size: 0.8rem; }
.sp-proj-row:hover .sp-proj-go { color: var(--theme-color-brand-primary, #156dd1); }
.sp-proj-note { padding: 0.85rem 0.9rem; font-size: 0.88rem; color: var(--theme-color-text-muted, #8a94a3); }
.sp-proj-cap { border-top: 1px solid var(--theme-color-border-light, #eef1f5); font-style: italic; }
.sp-proj-err { color: var(--theme-color-status-danger, #c0392b); }
.sampleplan-state { max-width: 720px; margin: 2rem auto; text-align: center; color: var(--theme-color-text-muted, #6b7686); }
.sp-d-headrow { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.sp-d-subkind { font-size: 0.78rem; color: var(--theme-color-text-muted, #8a94a3); margin-top: 0.2rem; }
.sp-d-editbtn { flex: 0 0 auto; display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.8rem; font-weight: 600; cursor: pointer;
	padding: 0.28rem 0.7rem; border-radius: 6px; border: 1px solid var(--theme-color-brand-primary, #156dd1); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-brand-primary, #156dd1); }
.sp-d-editbtn:hover { background: var(--theme-color-brand-primary, #156dd1); color: #fff; }
.sp-d-field-wide { grid-column: 1 / -1; }
.sp-d-fval-long { font-weight: 400; white-space: pre-wrap; line-height: 1.45; }
.sp-d-loadingnote { color: var(--theme-color-text-muted, #9aa3b1); font-size: 0.85rem; padding: 0.4rem 0; }
.sp-d-loadfail { color: var(--theme-color-status-danger, #c0392b); }
.sp-d-loadfail a { color: var(--theme-color-brand-primary, #156dd1); cursor: pointer; text-decoration: underline; }
.sp-d-form { display: flex; flex-direction: column; gap: 0.7rem; margin-bottom: 0.9rem; }
.sp-d-editrow { display: flex; flex-direction: column; gap: 0.28rem; }
.sp-ed-pending { font-style: italic; font-weight: 500; font-size: 0.78rem; color: var(--theme-color-text-muted, #8a94a3); }
.sp-d-editrow .sp-d-flabel { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-text-muted, #8a94a3); font-weight: 600; }
.sp-d-overhead { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.sp-d-input, .sp-d-select, .sp-d-textarea { width: 100%; box-sizing: border-box; font: inherit; font-size: 0.88rem; padding: 0.42rem 0.55rem;
	border: 1px solid var(--theme-color-border, #d6dbe3); border-radius: 6px; background: var(--theme-color-background-input, #fff); color: var(--theme-color-text-primary, #1f2733); }
.sp-d-input:focus, .sp-d-select:focus, .sp-d-textarea:focus { outline: none; border-color: var(--theme-color-brand-primary, #156dd1); box-shadow: 0 0 0 2px var(--theme-color-brand-primary-faded, rgba(21, 109, 209, 0.18)); }
.sp-d-textarea { resize: vertical; min-height: 2.4rem; }
.sp-d-computed { font-size: 0.86rem; color: var(--theme-color-text-secondary, #5a6573); background: var(--theme-color-background-tertiary, #f6f8fb); border-radius: 6px; padding: 0.42rem 0.55rem; }
.sp-d-checkinline, .sp-d-checkline { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; color: var(--theme-color-text-secondary, #5a6573); cursor: pointer; }
.sp-d-checkline { font-weight: 600; }
.sp-d-toggle { display: inline-flex; align-items: center; gap: 0.45rem; font-size: 0.85rem; color: var(--theme-color-text-secondary, #5a6573); }
.sp-d-actions { display: flex; gap: 0.5rem; justify-content: flex-end; border-top: 1px solid var(--theme-color-border-light, #eef1f5); padding-top: 0.7rem; }
.sp-d-btn { font-size: 0.85rem; font-weight: 600; cursor: pointer; padding: 0.42rem 1rem; border-radius: 6px; border: 1px solid var(--theme-color-border, #d6dbe3); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-text-secondary, #5a6573); }
.sp-d-btn:hover { background: var(--theme-color-background-secondary, #f4f6f9); }
.sp-d-btn[disabled] { opacity: 0.6; cursor: default; }
.sp-d-btn-primary { border-color: var(--theme-color-brand-primary, #156dd1); background: var(--theme-color-brand-primary, #156dd1); color: #fff; }
.sp-d-btn-primary:hover { background: var(--theme-color-brand-primary-dark, #115ab0); }
.sp-d-error { font-size: 0.84rem; color: var(--theme-color-status-danger, #c0392b); background: var(--theme-color-status-danger-faded, rgba(192, 57, 43, 0.08)); border: 1px solid var(--theme-color-status-danger, #c0392b); border-radius: 6px; padding: 0.45rem 0.6rem; margin-bottom: 0.7rem; }
.sp-doc-editbar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; margin-bottom: 0.8rem; padding-bottom: 0.6rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); }
.sp-doc-editbar-label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-muted, #8a94a3); }
.sp-doc-editbtn { display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.76rem; cursor: pointer; padding: 0.2rem 0.55rem; border-radius: 5px; border: 1px solid var(--theme-color-border, #d6dbe3); background: var(--theme-color-background-primary, #fff); color: var(--theme-color-brand-primary, #2E7D74); }
.sp-doc-editbtn:hover { background: var(--theme-color-background-secondary, #f4f6f9); }
/* Embedded in a ProjectWorkspace Materials tab: shed the standalone-page chrome (padding / centered container / big head). */
.section.sp-embedded { padding: 0; }
.sp-embedded .container { max-width: none; padding: 0; }
/* Drop the redundant project title (the workspace header + "Materials ▸ Sample Plan" breadcrumb already name it);
   keep the head bar for the Docs button. */
.sp-embedded .sampleplan-head { margin: 0 0 0.5rem; }
.sp-embedded .sampleplan-ic, .sp-embedded .sampleplan-title, .sp-embedded .sampleplan-sub { display: none; }
`,

	Templates:
	[
		{
			Hash: 'SamplePlan-Template',
			Template: html`
				<section class="section {~D:AppData.SamplePlan.EmbedClass~}">
					<div class="container">
						<div class="sampleplan-head">
							<span class="sampleplan-ic"><i class="fas fa-vials"></i></span>
							<div>
								<h1 class="sampleplan-title">{~D:AppData.SamplePlan.TitleText~}</h1>
								<p class="sampleplan-sub">{~D:AppData.SamplePlan.SubText~}</p>
							</div>
							{~TS:SamplePlan-DocsToggle:AppData.SamplePlan.PlanSlot~}
						</div>
						<div class="sampleplan-wrap">
							{~TS:SamplePlan-Loading:AppData.SamplePlan.LoadingSlot~}
							{~TS:SamplePlan-Error:AppData.SamplePlan.ErrorSlot~}
							{~TS:SamplePlan-Picker:AppData.SamplePlan.PickerSlot~}
							{~TS:SamplePlan-Plan:AppData.SamplePlan.PlanSlot~}
						</div>
					</div>
				</section>`,
		},
		{ Hash: 'SamplePlan-DocsToggle', Template: html`<button type="button" class="sampleplan-docs" onclick="_Pict.views.SamplePlan.openDocs()" title="Open the sampling-manual documentation panel"><i class="fas fa-book"></i> Docs</button>` },
		{ Hash: 'SamplePlan-Loading', Template: html`<div class="sampleplan-state"><i class="fas fa-spinner fa-spin"></i> Loading the sample plan…</div>` },
		{ Hash: 'SamplePlan-Error', Template: html`<div class="sampleplan-state"><i class="fas fa-triangle-exclamation"></i> {~D:Record.Message~}</div>` },
		{
			Hash: 'SamplePlan-Picker',
			Template: html`
				<div class="sampleplan-picker">
					<h2 class="sp-proj-h">Open a project’s sample plan</h2>
					<p class="sp-proj-sub">Pick a project from the list below, or type to filter by name.</p>
					<input id="SamplePlan-ProjectFilter" class="sp-proj-filter" type="text" autocomplete="off" placeholder="Filter projects by name…" oninput="_Pict.views.SamplePlan.onProjectFilter(this.value)" />
					<div id="SamplePlan-ProjectList" class="sp-proj-list"><div class="sp-proj-note"><i class="fas fa-spinner fa-spin"></i> Loading projects…</div></div>
				</div>`,
		},
		{
			Hash: 'SamplePlan-Plan',
			Template: html`
				<div class="sp-split">
					<div id="SamplePlan-Tree" class="sp-tree"></div>
					<div id="SamplePlan-Detail" class="sp-detail"><div class="sp-d-empty">Select any row to see its detail, satisfaction, and documentation.</div></div>
				</div>`,
		},
	],

	Renderables:
	[
		{
			RenderableHash: 'SamplePlanWrap',
			TemplateHash: 'SamplePlan-Template',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
		},
	],
};
